import { memo, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { toast } from "react-toastify";
import type { Patient } from "fhir/r5";

import { useSessionContext } from "@/contexts/SessionContext";
import { useTaskWatchingContext } from "@/contexts/TaskWatchingContext";

import {
  QuestionnaireRespondView,
  useQuestionnaireRespond,
  type QuestionnaireRespondSubmitSuccessMeta,
  isQuestionnaireRespondLegacyLocationState,
} from "src-next/modules/questionnaire-builder";
import {
  postQuestionnaireAnsweredNotificationTask,
  questionnaireTaskWorkflowService,
} from "src-next/modules/questionnaire-builder/questionnaire-tasks";

function getPatientDisplayName(patient: Patient | undefined): string {
  const fromText = patient?.name?.[0]?.text?.trim();
  return fromText || "Paciente";
}

export const QuestionnaireRespondPage = memo(function QuestionnaireRespondPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userPatient } = useSessionContext();
  const { updateTaskInContext } = useTaskWatchingContext();

  const locationState = useMemo(() => {
    if (!isQuestionnaireRespondLegacyLocationState(location.state)) return null;
    return location.state;
  }, [location.state]);

  const handleLeave = useCallback(() => {
    navigate("/home");
  }, [navigate]);

  if (!locationState) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 780, mx: "auto" }}>
        <Stack spacing={2}>
          <Alert severity="error">
            Acesso inválido. Abra esta tela pelo painel do paciente para carregar
            o questionário com os parâmetros obrigatórios.
          </Alert>
        </Stack>
      </Box>
    );
  }

  const patientName = getPatientDisplayName(userPatient);

  const respondentContextHint = useMemo(() => {
    if (!locationState.authorReference?.trim()) return undefined;
    const name = locationState.subjectPatientDisplayName?.trim();
    return name
      ? `Você está respondendo em nome de ${name}. As respostas serão registradas no prontuário desse dependente.`
      : "Você está respondendo por um dependente vinculado à sua conta. As respostas serão registradas no prontuário dele(a).";
  }, [locationState.authorReference, locationState.subjectPatientDisplayName]);

  const onSubmitSuccess = useCallback((_meta: QuestionnaireRespondSubmitSuccessMeta) => {
    void (async () => {
      const {
        patientTaskId,
        requesterPractitionerRoleReference,
        ownerOrganizationReference,
        questionnaireId,
        questionnaireTitle,
      } = locationState;

      try {
        await questionnaireTaskWorkflowService.patchQuestionnaireTaskMarkCompleted(
          patientTaskId,
        );
        updateTaskInContext(patientTaskId, { status: "completed" });
      } catch (error) {
        console.error("QuestionnaireRespondPage patch task", error);
        toast.error("Não foi possível marcar a task do paciente como respondida.");
      }

      try {
        await postQuestionnaireAnsweredNotificationTask({
          patientDisplayName: patientName,
          questionnaireTitle,
          questionnaireId,
          subjectReference: locationState.subjectReference,
          requesterPractitionerRoleReference,
          ownerOrganizationReference,
        });
      } catch (error) {
        console.error("QuestionnaireRespondPage create notification task", error);
        toast.error(
          "Resposta enviada, mas não foi possível notificar a equipe via task.",
        );
      } finally {
        navigate("/home");
      }
    })();
  }, [locationState, navigate, patientName, updateTaskInContext]);

  const hookResult = useQuestionnaireRespond({
    questionnaireId: locationState.questionnaireId,
    subjectReference: locationState.subjectReference,
    ...(locationState.appointmentReference
      ? { appointmentReference: locationState.appointmentReference }
      : {}),
    ...(locationState.authorReference?.trim()
      ? { authorReference: locationState.authorReference.trim() }
      : {}),
    onSubmitSuccess,
  });

  return (
    <QuestionnaireRespondView
      {...hookResult}
      variant="patientPortal"
      onLeavePlatform={handleLeave}
      respondentContextHint={respondentContextHint}
    />
  );
});

